import SysRecord from "app/class/record";
import useRecordListNavbarActions from "../hooks/useRecordListActions";
import useRecordListActionCompiler from "../hooks/useRecordListActionCompiler";
import { useSession } from "app/hooks/useSession";
import { useRecordListContext } from "../hooks/useRecordListContext";
import { useState } from "react";
import FormActionConfirmationDialog, { requiresFormActionConfirmation } from "modules/record/shared/components/FormActionConfirmationDialog";
import ResponsiveActionBar from "modules/record/shared/components/ResponsiveActionBar";
import useActionHotkeys from "modules/record/shared/hooks/useActionHotkeys";
import { allowsRecordAction } from "modules/record/shared/services/actionPermissions";

interface RecordListActionsComponentProps {
    tableName: string;
}

const RecordListNavbarActionsComponent = ({ tableName }: RecordListActionsComponentProps) => {
    const { data: userSession } = useSession();
    const { selectedIds, permissions } = useRecordListContext();
    const { actions } = useRecordListNavbarActions(tableName, {
        currentTableName: tableName,
        selectedCount: selectedIds.length,
        hasSelection: selectedIds.length > 0,
        user: { id: userSession?.id || "", roles: userSession?.roles || [] }
    });
    const permittedActions = actions.filter(action => allowsRecordAction(action, permissions));
    const { runActionScript } = useRecordListActionCompiler();
    const [pendingAction, setPendingAction] = useState<SysRecord | null>(null);
    const [runningActionId, setRunningActionId] = useState<string | null>(null);

    const executeAction = async (action: SysRecord) => {
        setRunningActionId(action.id);

        try {
            await runActionScript(action);
        } finally {
            setRunningActionId(null);
        }
    };

    const handleActionClick = (action: SysRecord) => {
        if (runningActionId) return;

        if (requiresFormActionConfirmation(action)) {
            setPendingAction(action);
            return;
        }

        executeAction(action);
    };

    const handleConfirmAction = async () => {
        const action = pendingAction;
        setPendingAction(null);

        if (action) {
            await executeAction(action);
        }
    };

    useActionHotkeys({
        actions: permittedActions,
        runningActionId,
        onActionTrigger: handleActionClick,
    });

    return (
        <>
            <ResponsiveActionBar
                actions={permittedActions}
                runningActionId={runningActionId}
                mobileVisibleCount={1}
                variant="inline"
                onActionClick={handleActionClick}
            />
            <FormActionConfirmationDialog
                action={pendingAction}
                open={Boolean(pendingAction)}
                onCancel={() => setPendingAction(null)}
                onConfirm={handleConfirmAction}
            />
        </>
    );
};

export default RecordListNavbarActionsComponent;
